import { Sparkles, Mic, Activity, Eye, MessageSquare, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * "A Legacy of Impact" — premium two-column About section that establishes
 * The Floor as a flagship initiative of the Nachrist Impact Sphere.
 */
const AboutTheBrand = () => {
  return (
    <section
      data-testid="about-the-brand"
      className="relative border-y border-sovereign-gold/20 bg-gradient-to-b from-background via-background to-secondary/40 px-6 py-20"
    >
      <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
        {/* Left: heading */}
        <div className="flex flex-col justify-center">
          <span className="mb-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sovereign-gold">
            <Sparkles className="h-3 w-3" /> About the Brand
          </span>
          <h2 className="font-serif text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
            A Legacy of <span className="text-gradient-gold">Impact</span>.
          </h2>
          <p className="mt-4 max-w-md text-sm text-muted-foreground">
            From a single visionary roof to a global commitment to progress.
          </p>
        </div>

        {/* Right: body */}
        <div className="flex flex-col justify-center">
          <p className="text-base leading-relaxed text-foreground/90 md:text-lg">
            <strong className="font-bold text-foreground">The Floor</strong> is a flagship initiative of the{" "}
            <strong className="font-bold text-sovereign-gold">Nachrist Impact Sphere</strong>. Founded by{" "}
            <strong className="font-bold text-foreground">Les Nachrist</strong>, the Sphere was created to
            unify strategic global solutions under one visionary roof. By combining innovation with long-term
            legacy planning, we ensure that every project we launch—including The Floor—operates at the
            highest level of excellence and strategic purpose.
          </p>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            We are more than a company; we are a{" "}
            <strong className="font-semibold text-foreground">global commitment to progress</strong>.
          </p>
        </div>
      </div>

      {/* Purpose: Self-Debate Preparation */}
      <div
        data-testid="about-self-debate-prep"
        className="mx-auto mt-20 max-w-[1100px] rounded-2xl border border-sovereign-gold/20 bg-card/40 p-8 backdrop-blur md:p-12"
      >
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
          <div className="flex flex-col justify-center">
            <span className="mb-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sovereign-gold">
              <Mic className="h-3 w-3" /> Purpose · Self-Debate Preparation
            </span>
            <h3 className="font-serif text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl">
              Rehearse the Stage <span className="text-gradient-gold">Before You Take It</span>.
            </h3>
            <p className="mt-4 max-w-md text-sm text-muted-foreground">
              Built for anyone running for office — from city council to the presidency — and for the
              advisors who prepare them.
            </p>
          </div>

          <div className="flex flex-col justify-center">
            <p className="text-base leading-relaxed text-foreground/90 md:text-lg">
              <strong className="font-bold text-foreground">The Floor</strong> is also a private,
              high-fidelity <strong className="font-semibold text-sovereign-gold">self-debate
              preparation studio</strong>. Candidates can record themselves at home, simulate the
              cadence of a live debate, and review their performance in granular detail —{" "}
              <strong className="font-semibold text-foreground">gesture, vocal tone, delivery, and
              content quality</strong> — long before they ever step on stage.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Every session is analyzed and turned into actionable corrections, so you walk into the
              real debate already rehearsed, refined, and ready.
            </p>

            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <li className="flex items-start gap-3 rounded-lg border border-sovereign-gold/15 bg-background/40 p-3">
                <Activity className="mt-0.5 h-4 w-4 text-sovereign-gold" />
                <span className="text-sm text-foreground/85">
                  <strong className="font-semibold text-foreground">Gesture analysis</strong> — posture, hand cues, eye contact
                </span>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-sovereign-gold/15 bg-background/40 p-3">
                <Mic className="mt-0.5 h-4 w-4 text-sovereign-gold" />
                <span className="text-sm text-foreground/85">
                  <strong className="font-semibold text-foreground">Vocal tone & pacing</strong> — pitch, energy, filler words
                </span>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-sovereign-gold/15 bg-background/40 p-3">
                <Eye className="mt-0.5 h-4 w-4 text-sovereign-gold" />
                <span className="text-sm text-foreground/85">
                  <strong className="font-semibold text-foreground">Delivery review</strong> — confidence, presence, composure
                </span>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-sovereign-gold/15 bg-background/40 p-3">
                <MessageSquare className="mt-0.5 h-4 w-4 text-sovereign-gold" />
                <span className="text-sm text-foreground/85">
                  <strong className="font-semibold text-foreground">Content coaching</strong> — argument strength, fact alignment
                </span>
              </li>
            </ul>

            {/* Deep-link CTA into the prep workflow */}
            <div className="mt-7">
              <Link to="/oratory-studio?source=about-self-debate" data-testid="about-self-debate-cta">
                <Button variant="sovereign" size="lg" className="gap-2">
                  Start Rehearsing in Oratory Studio
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <p className="mt-2 text-xs text-muted-foreground">
                Opens the AI Coach prep workflow — pick a template, record, and review.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutTheBrand;
